// src/App.tsx - Version mise à jour avec code splitting et PWA

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LayoutShellProvider } from './contexts/LayoutShellContext';
import { LocaleProvider, LocaleAuthSync } from './contexts/LocaleContext';
import './i18n';
import { usePermissions } from './hooks/usePermissions';
import { useTrackPageView } from './hooks/useTrackPageView';
import { isFeatureEnabled } from './types/business';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import Documentation from './features/documentation/component';
import NetworkStatusBanner from './components/NetworkStatusBanner/NetworkStatusBanner';
import UpdateAvailableBanner from './components/UpdateAvailableBanner/UpdateAvailableBanner';
import OfflineSyncBanner from './components/OfflineSyncBanner/OfflineSyncBanner';
import PreferredLocaleModal from './components/Locale/PreferredLocaleModal';
import NotificationDeepLinkHandler from './components/Notifications/NotificationDeepLinkHandler';
import { HelpTourAutoPrompt, ProductTourProvider } from './features/product-tours';
import { useServiceWorkerUpdate } from './hooks/useServiceWorkerUpdate';
import { useRefetchOnAppResume } from './hooks/useRefetchOnAppResume';
import './App.css';

// Import du QueryClient configuré
import { queryClientNoRetry, REFETCH_QUERIES_EVENT } from './config/reactQuery';
import { OfflineSyncProvider } from './contexts/OfflineSyncContext';
import { AttachmentPreviewProvider } from './contexts/AttachmentPreviewContext';

// ✅ PHASE 2.1 & 2.2 : Import SuperAdmin (chargé immédiatement - petit)
import SuperAdminLayout from './components/Layout/SuperAdminLayout';
import SuperAdminDashboard from './features/super-admin/SuperAdminDashboard';
import UsersManagement from './features/super-admin/UsersManagement';
import BusinessRoleProfiles from './features/super-admin/BusinessRoleProfiles';
import BusinessDetailPage from './features/super-admin/BusinessDetailPage';
import BusinessCreatePage from './features/super-admin/BusinessCreatePage';
import InstallationRequestsPipeline from './features/super-admin/InstallationRequestsPipeline';
import PickupCitiesAdmin from './features/super-admin/PickupCitiesAdmin';
import AdoptionUsagePage from './features/super-admin/AdoptionUsagePage';

// ✅ PHASE 2.2 : Code splitting - Routes principales avec React.lazy
// Pages critiques (chargées immédiatement)
import Login from './features/login/component';
import BusinessSelection from './features/business-selection/component';
import BeautyLayout from './components/Layout/GenericLayout';

import {
  AccountingDashboard,
  AccountingJournals,
  ActivityReports,
  ActivityTypes,
  AdaptiveDashboard,
  AppLaunchLanding,
  Appointments,
  AppointmentRequests,
  CampaignDetail,
  CashReports,
  Categories,
  ClientDetail,
  Clients,
  ClientTypes,
  CustomerLanding,
  Deliveries,
  Employees,
  ExpenseCategories,
  ExpenseNotes,
  Expenses,
  ExternalDestinations,
  ExternalDispatches,
  InstitutLeNidDoux,
  InventoryAudit,
  InventoryAuditReports,
  InventoryAuditSession,
  InventoryCountingInterface,
  InventoryDashboard,
  ManufacturingOrders,
  Marketing,
  MessagesClients,
  MyAccount,
  MyPayment,
  Opportunities,
  OpportunityCategories,
  PackagingUnits,
  PaymentPlans,
  LoyaltyPass,
  Payrolls,
  PerformanceIndicators,
  PickupInbox,
  PickupPublicLayout,
  PickupHomePage,
  PickupFindPage,
  PickupTrackPage,
  PickupJoinPage,
  PickupDirectPage,
  PickupSettings,
  Pipelines,
  PrestationsHistory,
  Products,
  InventoryLosses,
  InvoicingSettings,
  NotificationsSettings,
  SiteStatsPage,
  SettingsHub,
  PromoLanding,
  PromoResolver,
  ReferentDetail,
  Referents,
  RoleProfiles,
  SalesHistory,
  ServiceCategories,
  Services,
  CatalogPacks,
  StockMovements,
  Suppliers,
  TaxReturns,
  TransactionPage,
  HelpPage,
  HelpTicketPage,
  SupportInboxPage,
  SupportInboxTicketPage,
} from './features/lazy-app-pages';

// Routes inbox support (SA / CSM) — à étaler dans chaque <Routes> (enfants directs requis)
const supportTicketAdminRouteElements = [
  <Route
    key="support-tickets"
    path="/super-admin/support-tickets"
    element={
      <SuperAdminLayout>
        <Suspense fallback={<LoadingSpinner />}>
          <SupportInboxPage />
        </Suspense>
      </SuperAdminLayout>
    }
  />,
  <Route
    key="support-ticket-detail"
    path="/super-admin/support-tickets/:ticketId"
    element={
      <SuperAdminLayout>
        <Suspense fallback={<LoadingSpinner />}>
          <SupportInboxTicketPage />
        </Suspense>
      </SuperAdminLayout>
    }
  />,
];

// Page accès non autorisé
const UnauthorizedPage: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column',
    alignItems: 'center', 
    justifyContent: 'center',
    height: '50vh',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
    <h2>Accès non autorisé</h2>
    <p>Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
  </div>
);

// Routes Business
const BusinessRoutes: React.FC = () => {
  const permissions = usePermissions();
  const { currentBusiness } = useAuth();
  useTrackPageView();
  
  // Si permissions non chargées, afficher un loading
  if (!permissions || !currentBusiness) {
    return <LoadingSpinner />;
  }

  const canViewExternalDispatches =
    !!permissions?.canViewExternalDispatches &&
    isFeatureEnabled(currentBusiness, 'inventoryExternalDispatch');
  const canViewExternalDestinations =
    !!permissions?.canViewExternalDestinations &&
    isFeatureEnabled(currentBusiness, 'inventoryExternalDispatch');
  const canViewInventoryLosses =
    !!permissions?.canViewInventoryLosses &&
    isFeatureEnabled(currentBusiness, 'inventoryLosses');

  const canAccessPaymentPlansModule = !!permissions?.canViewPaymentPlans;
  const canAccessLoyaltyPassModule =
    !!permissions?.canViewLoyaltyPass &&
    isFeatureEnabled(currentBusiness, 'loyaltyPass');

  // Route par défaut selon le rôle et les permissions
  const getDefaultRoute = () => {
    if (permissions?.canViewDashboard) return '/dashboard';
    if (
      (permissions?.canViewPrestations && isFeatureEnabled(currentBusiness, 'services')) ||
      permissions?.canViewProductSales
    ) {
      return '/pos/transaction';
    }
    if (permissions?.canViewManufacturingOrders) return '/manufacturing/orders';
    if (permissions?.canViewClients) return '/clients';
    if (permissions?.canViewMessagesClients && isFeatureEnabled(currentBusiness, 'messagesClients')) return '/messages-clients';
    if (permissions?.canViewAppointments) return '/appointments';
    if (permissions?.canViewMarketing) return '/marketing';
    if (permissions?.canViewInventoryReports) return '/inventory/dashboard';
    if (permissions?.canViewCategories) return '/inventory/categories';
    if (permissions?.canViewProducts) return '/inventory/products';
    if (permissions?.canViewStockMovements) return '/inventory/movements';
    if (canViewExternalDispatches) return '/inventory/external-dispatches';
    if (canViewExternalDestinations) return '/inventory/external-destinations';
    if (permissions?.canViewInventoryAudit) return '/inventory/audit';
    if (permissions?.canViewServices) return '/services';
    if (permissions?.canViewAccounting && isFeatureEnabled(currentBusiness, 'accounting')) {
      return '/accounting/dashboard';
    }
    return '/services';
  };

  return (
    <BeautyLayout>
      <Routes>
        {/* Mon compte / Mes droits : accessible à tout utilisateur connecté */}
        <Route path="mon-compte" element={
          <Suspense fallback={<LoadingSpinner />}>
            <MyAccount />
          </Suspense>
        } />

        <Route path="mon-paiement" element={
          <Suspense fallback={<LoadingSpinner />}>
            <MyPayment />
          </Suspense>
        } />

        <Route path="aide" element={
          <Suspense fallback={<LoadingSpinner />}>
            <HelpPage />
          </Suspense>
        } />
        <Route path="aide/:ticketId" element={
          <Suspense fallback={<LoadingSpinner />}>
            <HelpTicketPage />
          </Suspense>
        } />

        {/* Routes Business existantes */}
        {permissions?.canViewDashboard && (
          <Route path="dashboard" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdaptiveDashboard />
            </Suspense>
          } />
        )}

        {(permissions?.canViewAppointments || permissions?.canViewAppointmentRequests) && (
          <>
            {permissions?.canViewAppointments && (
              <Route path="appointments" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Appointments />
                </Suspense>
              } />
            )}
            {permissions?.canViewAppointmentRequests && (
              <Route path="appointment-requests" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <AppointmentRequests />
                </Suspense>
              } />
            )}
          </>
        )}

        {permissions?.canViewPickups && isFeatureEnabled(currentBusiness, 'pickup') && (
          <>
            <Route path="pickups" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PickupInbox />
              </Suspense>
            } />
            {permissions?.canManagePickups && (
              <Route path="pickups/settings" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PickupSettings />
                </Suspense>
              } />
            )}
          </>
        )}

        {/* Routes Point de Vente */}
        {((permissions?.canViewPrestations && isFeatureEnabled(currentBusiness, 'services')) ||
          permissions?.canViewProductSales) &&
          isFeatureEnabled(currentBusiness, 'pos') && (
          <>
            <Route path="pos/transaction" element={
              <Suspense fallback={<LoadingSpinner />}>
                <TransactionPage />
              </Suspense>
            } />
            {permissions?.canViewPrestations && isFeatureEnabled(currentBusiness, 'services') && (
              <Route path="pos/prestations-history" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PrestationsHistory />
                </Suspense>
              } />
            )}
            {permissions?.canViewProductSales && (
              <Route path="pos/sales-history" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <SalesHistory />
                </Suspense>
              } />
            )}
            {(permissions?.canViewCashReports ||
              (permissions?.canViewPrestations && isFeatureEnabled(currentBusiness, 'services')) ||
              permissions?.canViewProductSales) && (
              <Route path="pos/reports" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CashReports />
                </Suspense>
              } />
            )}
            <Route path="pos" element={<Navigate to="pos/transaction" replace />} />
          </>
        )}

        {permissions?.canViewInvoicing && (
          <Route path="settings/invoicing" element={
            <Suspense fallback={<LoadingSpinner />}>
              <InvoicingSettings />
            </Suspense>
          } />
        )}

        <Route path="settings/notifications" element={
          <Suspense fallback={<LoadingSpinner />}>
            <NotificationsSettings />
          </Suspense>
        } />

        <Route path="site-stats" element={
          <Suspense fallback={<LoadingSpinner />}>
            <SiteStatsPage />
          </Suspense>
        } />
        <Route path="settings/site-stats" element={<Navigate to="/site-stats" replace />} />

        <Route path="settings" element={
          <Suspense fallback={<LoadingSpinner />}>
            <SettingsHub />
          </Suspense>
        } />

        {/* Routes Fabrication */}
        {permissions?.canViewManufacturingOrders && (
          <Route path="manufacturing/orders" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ManufacturingOrders />
            </Suspense>
          } />
        )}

        {permissions?.canViewPrestations && isFeatureEnabled(currentBusiness, 'services') && (
          <Route path="prestations" element={<Navigate to="pos/prestations-history" replace />} />
        )}
        
        {permissions?.canViewClients && (
          <>
            <Route path="clients" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Clients />
              </Suspense>
            } />
            <Route path="clients/:clientId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ClientDetail />
              </Suspense>
            } />
          </>
        )}
        {canAccessPaymentPlansModule && (
          <>
            <Route path="payment-plans/create" element={<Navigate to="/pos/transaction" replace />} />
            <Route path="payment-plans" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PaymentPlans />
              </Suspense>
            } />
          </>
        )}
        {canAccessLoyaltyPassModule && (
          <>
            <Route
              path="settings/loyalty-pass"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <LoyaltyPass />
                </Suspense>
              }
            />
            <Route path="loyalty-pass" element={<Navigate to="/settings/loyalty-pass" replace />} />
          </>
        )}
        {permissions?.canViewReferents && (
          <>
            <Route path="referents" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Referents />
              </Suspense>
            } />
            <Route path="referents/:referentId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ReferentDetail />
              </Suspense>
            } />
          </>
        )}
        {permissions?.canViewMarketing && (
          <>
            <Route path="marketing" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Marketing />
              </Suspense>
            } />
            <Route path="marketing/campaign/:campaignId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <CampaignDetail />
              </Suspense>
            } />
          </>
        )}
        {permissions?.canViewMessagesClients && isFeatureEnabled(currentBusiness, 'messagesClients') && (
          <Route path="messages-clients" element={
            <Suspense fallback={<LoadingSpinner />}>
              <MessagesClients />
            </Suspense>
          } />
        )}
        {permissions?.canViewEmployees && (
          <Route path="employees" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Employees />
            </Suspense>
          } />
        )}
        {permissions?.canViewRoleProfiles && (
          <Route path="settings/role-profiles" element={
            <Suspense fallback={<LoadingSpinner />}>
              <RoleProfiles />
            </Suspense>
          } />
        )}
        {permissions?.canViewPipelines && isFeatureEnabled(currentBusiness, 'pipelineConfig') && (
          <Route path="settings/pipeline" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Pipelines />
            </Suspense>
          } />
        )}
        {permissions?.canViewActivityReports && isFeatureEnabled(currentBusiness, 'activityReports') && (
          <Route path="activity/reports" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ActivityReports />
            </Suspense>
          } />
        )}
        {permissions?.canViewActivityTypes && isFeatureEnabled(currentBusiness, 'activityTypes') && (
          <Route path="activity/activity-types" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ActivityTypes />
            </Suspense>
          } />
        )}
        {permissions?.canViewSalesPipeline && isFeatureEnabled(currentBusiness, 'opportunities') && (
          <Route path="activity/opportunities" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Opportunities />
            </Suspense>
          } />
        )}
        {permissions?.canViewClientTypes && isFeatureEnabled(currentBusiness, 'clientTypes') && (
          <Route path="activity/client-types" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ClientTypes />
            </Suspense>
          } />
        )}
        {permissions?.canViewOpportunityCategories && isFeatureEnabled(currentBusiness, 'opportunityCategories') && (
          <Route path="activity/opportunity-categories" element={
            <Suspense fallback={<LoadingSpinner />}>
              <OpportunityCategories />
            </Suspense>
          } />
        )}
        {permissions?.canViewPayrolls && (
          <Route path="payrolls" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Payrolls />
            </Suspense>
          } />
        )}
        {permissions?.canViewCatalogPacks && isFeatureEnabled(currentBusiness, 'catalogPacks') && (
          <>
            <Route
              path="settings/packs"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CatalogPacks />
                </Suspense>
              }
            />
            <Route path="packs" element={<Navigate to="/settings/packs" replace />} />
          </>
        )}
        {permissions?.canViewServices && isFeatureEnabled(currentBusiness, 'services') && (
          <>
            <Route path="services" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Services />
              </Suspense>
            } />
            <Route path="services/categories" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ServiceCategories />
              </Suspense>
            } />
          </>
        )}

        {/* Routes Inventory - chaque feature pilotée par sa permission */}
        {(permissions?.canViewProducts || permissions?.canViewCategories || permissions?.canViewStockMovements || canViewExternalDispatches || canViewExternalDestinations || canViewInventoryLosses || permissions?.canViewInventoryAudit || permissions?.canViewInventoryReports) && (
          <>
            {permissions?.canViewInventoryReports && (
              <Route path="inventory/dashboard" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <InventoryDashboard />
                </Suspense>
              } />
            )}
            {permissions?.canViewCategories && (
              <Route path="inventory/categories" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Categories />
                </Suspense>
              } />
            )}
            {permissions?.canViewProducts && (
              <Route path="inventory/products" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Products />
                </Suspense>
              } />
            )}
            {permissions?.canViewProducts && (
              <Route path="inventory/packaging-units" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PackagingUnits />
                </Suspense>
              } />
            )}
            {permissions?.canViewStockMovements && (
              <Route path="inventory/movements" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <StockMovements />
                </Suspense>
              } />
            )}
            {canViewInventoryLosses && (
              <Route path="inventory/losses" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <InventoryLosses />
                </Suspense>
              } />
            )}
            {canViewExternalDispatches && (
              <Route path="inventory/external-dispatches" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ExternalDispatches />
                </Suspense>
              } />
            )}
            {canViewExternalDestinations && (
              <Route path="inventory/external-destinations" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ExternalDestinations />
                </Suspense>
              } />
            )}
            {permissions?.canViewProductSales && (
              <Route path="inventory/sales" element={<Navigate to="pos/sales-history" replace />} />
            )}

            {/* ✅ NOUVELLES : Routes Inventory Audit */}
            {permissions?.canViewInventoryAudit && (
              <>
                {/* Page principale des inventaires */}
                <Route path="inventory/audit" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <InventoryAudit />
                  </Suspense>
                } />
                
                {/* Détail d'une session d'inventaire */}
                <Route path="inventory/audit/session/:sessionId" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <InventoryAuditSession />
                  </Suspense>
                } />
                
                {/* Interface de comptage mobile */}
                {permissions?.canCountInventory && (
                  <Route path="inventory/audit/session/:sessionId/count" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <InventoryCountingInterface />
                    </Suspense>
                  } />
                )}

                {/* Rapport d'une session d'inventaire */}
                <Route path="inventory/audit/session/:sessionId/reports" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <InventoryAuditReports />
                  </Suspense>
                } />
              </>
            )}

            <Route 
              path="inventory" 
              element={
                <Navigate 
                  to={
                    permissions?.canViewInventoryReports ? "inventory/dashboard" :
                    permissions?.canViewCategories ? "inventory/categories" :
                    permissions?.canViewProducts ? "inventory/products" :
                    permissions?.canViewStockMovements ? "inventory/movements" :
                    canViewExternalDispatches ? "inventory/external-dispatches" :
                    canViewExternalDestinations ? "inventory/external-destinations" :
                    permissions?.canViewInventoryAudit ? "inventory/audit" :
                    "inventory/dashboard"
                  } 
                  replace 
                />
              } 
            />
          </>
        )}

        {/* Routes Comptabilité : même garde que le menu (feature + permission) */}
        {permissions?.canViewAccounting && isFeatureEnabled(currentBusiness, 'accounting') && (
          <>
            <Route path='accounting/dashboard' element={
              <Suspense fallback={<LoadingSpinner />}>
                <AccountingDashboard />
              </Suspense>
            } />
            <Route path='accounting/journals' element={
              <Suspense fallback={<LoadingSpinner />}>
                <AccountingJournals />
              </Suspense>
            } />
            <Route path='accounting/tax-returns' element={
              <Suspense fallback={<LoadingSpinner />}>
                <TaxReturns />
              </Suspense>
            } />
          </>
        )}

        {/* Routes Dépenses */}
        {permissions?.canViewExpenses && (
          <Route path='expenses' element={
            <Suspense fallback={<LoadingSpinner />}>
              <Expenses />
            </Suspense>
          } />
        )}
        {permissions?.canViewExpenseCategories && isFeatureEnabled(currentBusiness, 'expenseCategories') && (
          <Route path='expenses/categories' element={
            <Suspense fallback={<LoadingSpinner />}>
              <ExpenseCategories />
            </Suspense>
          } />
        )}

        {/* Routes Notes de frais */}
        {permissions?.canViewExpenseNotes && isFeatureEnabled(currentBusiness, 'expenseNotes') && (
          <Route path='expense-notes' element={
            <Suspense fallback={<LoadingSpinner />}>
              <ExpenseNotes />
            </Suspense>
          } />
        )}

        {/* Routes Indicateur de performance */}
        {permissions?.canViewPerformanceObjectives && isFeatureEnabled(currentBusiness, 'performanceIndicators') && (
          <Route path='performance-indicators' element={
            <Suspense fallback={<LoadingSpinner />}>
              <PerformanceIndicators />
            </Suspense>
          } />
        )}

        {/* Routes Suppliers */}
        {permissions?.canViewSuppliers && (
          <>
            <Route path="suppliers" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Suppliers />
              </Suspense>
            } />
            {permissions?.canViewDeliveries && (
              <Route path="suppliers/deliveries" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Deliveries />
                </Suspense>
              } />
            )}
          </>
        )}

        {/* Les routes sont protégées par les permissions, plus besoin de redirection */}
        <Route path="unauthorized" element={<UnauthorizedPage />} />
        {/* Entrée app après login (/ reste la vitrine publique) */}
        <Route path="/start" element={<Navigate to={getDefaultRoute()} replace />} />
        <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
      </Routes>
    </BeautyLayout>
  );
};

// Routes principales
const AppRoutes: React.FC = () => {
  const { user, userProfile, loading, isSuperAdmin, isCSM, needsBusinessSelection, currentBusiness } = useAuth();

  if (loading) return <LoadingSpinner />;

  if (!user || !userProfile) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Redirection Super Admin / CSM
  if (isSuperAdmin) {
    // 🔹 Super Admin :
    // - Sans business sélectionné → interface admin
    // - Avec business sélectionné (via switchToBusiness) → interface business
    if (currentBusiness) {
      return (
        <Routes>
          <Route path="/login" element={<Navigate to="/start" replace />} />
          <Route path="/business-selection" element={<BusinessSelection />} />
          {supportTicketAdminRouteElements}
          <Route path="/*" element={<BusinessRoutes />} />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/start" replace />} />
        <Route path="/start" element={<Navigate to="/super-admin/businesses" replace />} />
        <Route path="/super-admin/businesses" element={<SuperAdminLayout><SuperAdminDashboard /></SuperAdminLayout>} />
        <Route path="/super-admin/businesses/new" element={<SuperAdminLayout><BusinessCreatePage /></SuperAdminLayout>} />
        <Route path="/super-admin/businesses/:businessId" element={<SuperAdminLayout><BusinessDetailPage /></SuperAdminLayout>} />
        <Route path="/super-admin/businesses/:businessId/role-profiles" element={<SuperAdminLayout><BusinessRoleProfiles /></SuperAdminLayout>} />
        <Route path="/super-admin/users" element={<SuperAdminLayout><UsersManagement /></SuperAdminLayout>} />
        <Route path="/super-admin/adoption" element={<SuperAdminLayout><AdoptionUsagePage /></SuperAdminLayout>} />
        <Route path="/super-admin/installation-requests" element={<SuperAdminLayout><InstallationRequestsPipeline /></SuperAdminLayout>} />
        <Route path="/super-admin/pickup-cities" element={<SuperAdminLayout><PickupCitiesAdmin /></SuperAdminLayout>} />
        {supportTicketAdminRouteElements}
        <Route path="*" element={<Navigate to="/super-admin/businesses" replace />} />
      </Routes>
    );
  }

  if (isCSM) {
    // 🔹 CSM :
    // - Si aucun business sélectionné → page de sélection
    // - Si un business est sélectionné → routes normales du business
    if (!currentBusiness) {
      return (
        <Routes>
          <Route path="/login" element={<Navigate to="/business-selection" replace />} />
          <Route path="/start" element={<Navigate to="/business-selection" replace />} />
          <Route path="/business-selection" element={<BusinessSelection />} />
          {supportTicketAdminRouteElements}
          <Route path="*" element={<Navigate to="/business-selection" replace />} />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/start" replace />} />
        <Route path="/business-selection" element={<BusinessSelection />} />
        {supportTicketAdminRouteElements}
        <Route path="/*" element={<BusinessRoutes />} />
      </Routes>
    );
  }

  // Sélection de business requise pour utilisateurs multi-business
  if (needsBusinessSelection) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/business-selection" replace />} />
        <Route path="/start" element={<Navigate to="/business-selection" replace />} />
        <Route path="/business-selection" element={<BusinessSelection />} />
        <Route path="*" element={<Navigate to="/business-selection" replace />} />
      </Routes>
    );
  }

  // Routes pour utilisateurs normaux avec business sélectionné
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/start" replace />} />
      <Route path="/business-selection" element={<BusinessSelection />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/*" element={<BusinessRoutes />} />
    </Routes>
  );
};

// P4 : écoute du bouton « Réessayer » (refetch manuel)
const RefetchOnRetryListener: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    const handler = () => {
      queryClient.refetchQueries();
    };
    window.addEventListener(REFETCH_QUERIES_EVENT, handler);
    return () => window.removeEventListener(REFETCH_QUERIES_EVENT, handler);
  }, [queryClient]);
  return <>{children}</>;
};

// Composant pour gérer les mises à jour du service worker
const AppWithUpdateCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { updateAvailable, registration } = useServiceWorkerUpdate();
  useRefetchOnAppResume();

  const handleUpdate = () => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Attendre que le nouveau service worker prenne le contrôle
      registration.waiting.addEventListener('statechange', (e) => {
        const target = e.target as ServiceWorker;
        if (target.state === 'activated') {
          window.location.reload();
        }
      });
    } else {
      window.location.reload();
    }
  };

  return (
    <>
      {updateAvailable && (
        <UpdateAvailableBanner
          onUpdate={handleUpdate}
        />
      )}
      <NetworkStatusBanner />
      <OfflineSyncBanner />
      {children}
    </>
  );
};

/** Ancienne URL /ramassage/p/:slug → /pressing/ramassage/p/:slug */
const RedirectLegacyPickupSlug: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/pressing/ramassage/p/${encodeURIComponent(slug || '')}`} replace />;
};

/** Synchronise preferredLocale utilisateur → LocaleProvider (routes authentifiées). */
const LocaleAuthSyncBridge: React.FC = () => {
  const { user, userProfile } = useAuth();
  return (
    <LocaleAuthSync
      userId={user?.uid}
      preferredLocale={userProfile?.preferredLocale}
      profileReady={!!userProfile}
    />
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClientNoRetry}>
      <RefetchOnRetryListener>
      <LocaleProvider
        theme={{
          token: {
            colorPrimary: '#E91E63',
            borderRadius: 6,
          },
        }}
      >
        <AppWithUpdateCheck>
          <BrowserRouter>
            <Routes>
              {/* ===== ROUTES PUBLIQUES (SANS AuthProvider) ===== */}
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/p/:hash" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PromoResolver />
                </Suspense>
              } />
              <Route path="/page/:businessName" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CustomerLanding />
                </Suspense>
              } />
              <Route path="/promo/:businessId/:promotionId" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PromoLanding />
                </Suspense>
              } />
              
              {/* Landing pages clients */}
              <Route path="/institut-le-nid-doux" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <InstitutLeNidDoux />
                </Suspense>
              } />
              {/* Vitrine produit (ex-/lancement) */}
              <Route path="/" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <AppLaunchLanding />
                </Suspense>
              } />
              <Route path="/lancement" element={<Navigate to="/" replace />} />

              {/* Hub ramassage pressing */}
              <Route
                path="/pressing/ramassage"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PickupPublicLayout />
                  </Suspense>
                }
              >
                <Route index element={<PickupHomePage />} />
                <Route path="trouver" element={<PickupFindPage />} />
                <Route path="suivi" element={<PickupTrackPage />} />
                <Route path="rejoindre" element={<PickupJoinPage />} />
              </Route>
              <Route
                path="/pressing/ramassage/p/:slug"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PickupDirectPage />
                  </Suspense>
                }
              />
              <Route path="/ramassage" element={<Navigate to="/pressing/ramassage" replace />} />
              <Route path="/ramassage/p/:slug" element={<RedirectLegacyPickupSlug />} />
              
              {/* ===== ROUTES PRIVÉES (AVEC AuthProvider) ===== */}
              <Route path="/*" element={
                <AuthProvider>
                  <LocaleAuthSyncBridge />
                  <PreferredLocaleModal />
                  <LayoutShellProvider>
                    <ProductTourProvider>
                      <HelpTourAutoPrompt />
                      <AttachmentPreviewProvider>
                        <OfflineSyncProvider>
                          <NotificationDeepLinkHandler>
                            <AppRoutes />
                          </NotificationDeepLinkHandler>
                        </OfflineSyncProvider>
                      </AttachmentPreviewProvider>
                    </ProductTourProvider>
                  </LayoutShellProvider>
                </AuthProvider>
              } />
            </Routes>
          </BrowserRouter>
        </AppWithUpdateCheck>
      </LocaleProvider>
      </RefetchOnRetryListener>
    </QueryClientProvider>
  );
};

export default App;